import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Add, EventNote, Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useGetComsWeeklyReportsQuery,
  useBackfillComsWeeklyReportsMutation,
  WeeklyReportStatus,
} from "../../redux/comsApi";

const COMS_NAVY = "#123456";

const STATUS_LABEL: Record<string, string> = {
  "awaiting-site-qs": "Awaiting QS",
  "awaiting-site-manager": "Awaiting Site Manager",
  approved: "Approved",
};

const STATUS_COLOR: Record<
  string,
  "default" | "warning" | "info" | "success" | "error"
> = {
  "awaiting-site-qs": "warning",
  "awaiting-site-manager": "warning",
  approved: "success",
};

function statusChip(status?: string) {
  const s = status ?? "awaiting-site-qs";
  return (
    <Chip
      label={STATUS_LABEL[s] ?? s}
      color={STATUS_COLOR[s] ?? "default"}
      size="small"
    />
  );
}

const fmt = (v?: number) =>
  `$${(v ?? 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

interface Props {
  blockId: string;
}

const WeeklyReports: React.FC<Props> = ({ blockId }) => {
  const navigate = useNavigate();
  const {
    data: reports = [],
    isLoading,
    refetch,
  } = useGetComsWeeklyReportsQuery(blockId, {
    refetchOnMountOrArgChange: true,
  });
  const [backfill, { isLoading: isBackfilling }] =
    useBackfillComsWeeklyReportsMutation();
  const [backfillResult, setBackfillResult] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const dedupedReports = useMemo(() => {
    const byWeek = new Map<string, (typeof reports)[number]>();
    for (const r of reports) {
      const key = (r.weekStart ?? "").slice(0, 10);
      if (!byWeek.has(key)) byWeek.set(key, r);
    }
    return [...byWeek.values()];
  }, [reports]);

  const handleBackfill = async () => {
    setConfirmOpen(false);
    try {
      const result = await backfill({ blockId }).unwrap();
      setBackfillResult(
        `Regenerated ${result.generated} week${
          result.generated !== 1 ? "s" : ""
        }. All approval statuses have been reset.`,
      );
      refetch();
    } catch {
      setBackfillResult("Error regenerating reports. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h2" sx={{ color: COMS_NAVY, fontWeight: 600 }}>
          Weekly Reports
        </Typography>
        <Button
          variant="outlined"
          startIcon={
            isBackfilling ? <CircularProgress size={16} /> : <Refresh />
          }
          disabled={isBackfilling}
          onClick={() => setConfirmOpen(true)}
          sx={{
            borderColor: COMS_NAVY,
            color: COMS_NAVY,
            "&:hover": { bgcolor: "#f0f4f8" },
          }}
        >
          Regenerate All Weeks
        </Button>
      </Box>

      {backfillResult && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setBackfillResult(null)}
        >
          {backfillResult}
        </Alert>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Regenerate All Weekly Reports?</DialogTitle>
        <DialogContent>
          This will delete all existing weekly reports for this block and
          regenerate them from the current daily report data. Any previously
          approved reports will have their approval status reset.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBackfill}
            sx={{ bgcolor: COMS_NAVY }}
          >
            Regenerate All
          </Button>
        </DialogActions>
      </Dialog>

      {isBackfilling && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Regenerating weekly reports. This may take a moment...
        </Alert>
      )}

      {dedupedReports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <EventNote sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
          <Typography color="text.secondary">
            No weekly reports yet. Click "Regenerate All Weeks" to generate from
            daily report history.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: COMS_NAVY }}>
                {["Week", "Revenue", "Cost", "Profit / Loss", "Status", ""].map(
                  (h) => (
                    <TableCell
                      key={h}
                      sx={{ color: "#fff", fontWeight: 600, py: 1.5 }}
                    >
                      {h}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {dedupedReports.map((r) => {
                const pl = r.periodProfitLoss ?? 0;
                const isLoss = pl < 0;
                return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      bgcolor: isLoss ? "rgba(198,40,40,0.04)" : undefined,
                    }}
                    onClick={() =>
                      navigate(
                        `/coms/daily-site-report/blocks/${blockId}/weekly-reports/${r.id}`,
                      )
                    }
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {r.weekStart} → {r.weekEnd}
                    </TableCell>
                    <TableCell>{fmt(r.periodRevenue)}</TableCell>
                    <TableCell>
                      {fmt(
                        (r.periodMaterials ?? 0) +
                          (r.periodLabour ?? 0) +
                          (r.periodEquipment ?? 0),
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        color: isLoss ? "#c62828" : "#2e7d32",
                      }}
                    >
                      {isLoss ? "▼ " : "▲ "}
                      {fmt(pl)}
                    </TableCell>
                    <TableCell>{statusChip(r.status)}</TableCell>
                    <TableCell sx={{ color: COMS_NAVY, fontWeight: 500 }}>
                      View →
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default WeeklyReports;
